import { EVENT_META, activeMinutes, type BabyEvent } from './meta.ts';
import { summarizeDay, type StripSegment } from './summary.ts';
import { addDays, dayRange } from './time.ts';

/** Итоги одних суток — строка статистики за период. */
export interface DayStat {
  date: string;
  /** Есть ли в сутках хоть одна запись (для честных средних). */
  hasData: boolean;
  sleepMin: number;
  /** Сон с 20:00 до 08:00. */
  nightSleepMin: number;
  /** Дневной сон (08:00–20:00). */
  daySleepMin: number;
  /** Число засыпаний, начавшихся в эти сутки. */
  sleepCount: number;
  /** Сколько минут ушло на укладывания и сколько их было (только записи с «начали укладывать»). */
  settleMin: number;
  settleCount: number;
  /** Самый длинный непрерывный сон, начавшийся в эти сутки. */
  longestSleepMin: number;
  feedCount: number;
  breastCount: number;
  breastMin: number;
  bottleCount: number;
  bottleMl: number;
  solidCount: number;
  diaperCount: number;
  wetCount: number;
  dirtyCount: number;
  walkMin: number;
  walkCount: number;
  /** Максимальная температура за сутки. */
  maxTemp: number | null;
  medicineCount: number;
  noteCount: number;
  /** Полоса суток — только для коротких периодов. */
  strip?: StripSegment[];
}

const HOUR = 3600000;
const LOOKBACK = 48 * HOUR;

function overlap(a0: number, a1: number, b0: number, b1: number): number {
  return Math.max(0, Math.min(a1, b1) - Math.max(a0, b0)) / 60000;
}

/** Первый индекс отсортированного по началу массива с началом >= t. */
function lowerBound(starts: number[], t: number): number {
  let lo = 0;
  let hi = starts.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (starts[mid]! < t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/**
 * Статистика по дням за [from, to] включительно (даты 'YYYY-MM-DD' в поясе tz).
 * `events` — записи периода плюс ~2 суток до него (сон, начавшийся накануне).
 */
export function summarizeRange(
  events: readonly BabyEvent[],
  from: string,
  to: string,
  tz: string,
  now: number,
  opts: { strip?: boolean } = {},
): DayStat[] {
  const sorted = [...events].sort((a, b) => Date.parse(a.startedAt) - Date.parse(b.startedAt));
  const starts = sorted.map((e) => Date.parse(e.startedAt));
  const out: DayStat[] = [];

  for (let date = from; date <= to; date = addDays(date, 1)) {
    const range = dayRange(date, tz);
    const i0 = lowerBound(starts, range.start - LOOKBACK);
    const i1 = lowerBound(starts, range.end);
    const window = sorted.slice(i0, i1);
    const base = summarizeDay(window, range, date, now);
    const dayEnd = Math.min(range.end, Math.max(now, range.start));

    const st: DayStat = {
      date,
      hasData: false,
      sleepMin: base.sleepMinutes,
      nightSleepMin: 0,
      daySleepMin: 0,
      sleepCount: base.sleepCount,
      longestSleepMin: 0,
      settleMin: 0,
      settleCount: 0,
      feedCount: base.feedCount,
      breastCount: 0,
      breastMin: 0,
      bottleCount: 0,
      bottleMl: base.bottleMl,
      solidCount: 0,
      diaperCount: base.diaperCount,
      wetCount: 0,
      dirtyCount: 0,
      walkMin: base.walkMinutes,
      walkCount: 0,
      maxTemp: null,
      medicineCount: 0,
      noteCount: 0,
    };
    if (opts.strip) st.strip = base.strip;

    // Ночь — до 08:00 и после 20:00 по часам семьи.
    const morning = range.start + 8 * HOUR;
    const evening = range.start + 20 * HOUR;

    for (const e of window) {
      if (e.type === 'sleep' && e.payload.settling) continue;
      const s = Date.parse(e.startedAt);
      const end = e.endedAt ? Date.parse(e.endedAt) : EVENT_META[e.type].timed ? now : s;
      const startsInDay = s >= range.start && s < range.end;
      const touchesDay = EVENT_META[e.type].timed ? s < range.end && end > range.start : startsInDay;
      if (touchesDay) st.hasData = true;

      if (e.type === 'sleep') {
        const night = overlap(s, end, range.start, Math.min(morning, dayEnd)) + overlap(s, end, evening, dayEnd);
        st.nightSleepMin += night;
        if (startsInDay) st.longestSleepMin = Math.max(st.longestSleepMin, Math.round((end - s) / 60000));
        if (startsInDay && typeof e.payload.settleStartedAt === 'string') {
          st.settleMin += Math.max(0, Math.round((s - Date.parse(e.payload.settleStartedAt)) / 60000));
          st.settleCount += 1;
        }
      }
      if (!startsInDay) continue;
      const p = e.payload;
      switch (e.type) {
        case 'breast':
          st.breastCount += 1;
          st.breastMin += Math.round(activeMinutes(e, now));
          break;
        case 'bottle':
          st.bottleCount += 1;
          break;
        case 'solid':
          st.solidCount += 1;
          break;
        case 'diaper':
          if (p.kind === 'wet' || p.kind === 'mixed') st.wetCount += 1;
          if (p.kind === 'dirty' || p.kind === 'mixed') st.dirtyCount += 1;
          break;
        case 'walk':
          st.walkCount += 1;
          break;
        case 'temperature':
          if (typeof p.celsius === 'number') st.maxTemp = Math.max(st.maxTemp ?? 0, p.celsius);
          break;
        case 'medicine':
          st.medicineCount += 1;
          break;
        case 'note':
          st.noteCount += 1;
          break;
        default:
          break;
      }
    }
    st.nightSleepMin = Math.round(Math.min(st.nightSleepMin, st.sleepMin));
    st.daySleepMin = Math.max(0, st.sleepMin - st.nightSleepMin);
    out.push(st);
  }
  return out;
}

/* ---------- Нормы ВОЗ (Child Growth Standards), 0–24 мес ---------- */

/** Месяц → [−2 SD, медиана, +2 SD]. Таблицы ВОЗ «вес к возрасту» и «длина к возрасту», округлено до 0,1. */
type Band = Record<number, readonly [number, number, number]>;

export const WHO_WEIGHT_KG: Record<'male' | 'female', Band> = {
  male: {
    0: [2.5, 3.3, 4.4], 1: [3.4, 4.5, 5.8], 2: [4.3, 5.6, 7.1], 3: [5.0, 6.4, 8.0], 4: [5.6, 7.0, 8.7], 5: [6.0, 7.5, 9.3],
    6: [6.4, 7.9, 9.8], 7: [6.7, 8.3, 10.3], 8: [6.9, 8.6, 10.7], 9: [7.1, 8.9, 11.0], 10: [7.4, 9.2, 11.4], 11: [7.6, 9.4, 11.7],
    12: [7.7, 9.6, 12.0], 15: [8.3, 10.3, 12.8], 18: [8.8, 10.9, 13.7], 21: [9.2, 11.5, 14.5], 24: [9.7, 12.2, 15.3],
  },
  female: {
    0: [2.4, 3.2, 4.2], 1: [3.2, 4.2, 5.5], 2: [3.9, 5.1, 6.6], 3: [4.5, 5.8, 7.5], 4: [5.0, 6.4, 8.2], 5: [5.4, 6.9, 8.8],
    6: [5.7, 7.3, 9.3], 7: [6.0, 7.6, 9.8], 8: [6.3, 7.9, 10.2], 9: [6.5, 8.2, 10.5], 10: [6.7, 8.5, 10.9], 11: [6.9, 8.7, 11.2],
    12: [7.0, 8.9, 11.5], 15: [7.6, 9.6, 12.4], 18: [8.1, 10.2, 13.2], 21: [8.6, 10.9, 14.0], 24: [9.0, 11.5, 14.8],
  },
};

export const WHO_LENGTH_CM: Record<'male' | 'female', Band> = {
  male: {
    0: [46.1, 49.9, 53.7], 1: [50.8, 54.7, 58.6], 2: [54.4, 58.4, 62.4], 3: [57.3, 61.4, 65.5], 4: [59.7, 63.9, 68.0], 5: [61.7, 65.9, 70.1],
    6: [63.3, 67.6, 71.9], 7: [64.8, 69.2, 73.5], 8: [66.2, 70.6, 75.0], 9: [67.5, 72.0, 76.5], 10: [68.7, 73.3, 77.9], 11: [69.9, 74.5, 79.2],
    12: [71.0, 75.7, 80.5], 15: [74.1, 79.1, 84.2], 18: [76.9, 82.3, 87.7], 21: [79.4, 85.1, 90.9], 24: [81.7, 87.8, 93.9],
  },
  female: {
    0: [45.4, 49.1, 52.9], 1: [49.8, 53.7, 57.6], 2: [53.0, 57.1, 61.1], 3: [55.6, 59.8, 64.0], 4: [57.8, 62.1, 66.4], 5: [59.6, 64.0, 68.5],
    6: [61.2, 65.7, 70.3], 7: [62.7, 67.3, 71.9], 8: [64.0, 68.7, 73.5], 9: [65.3, 70.1, 75.0], 10: [66.5, 71.5, 76.4], 11: [67.7, 72.8, 77.8],
    12: [68.9, 74.0, 79.2], 15: [72.0, 77.5, 83.0], 18: [74.9, 80.7, 86.5], 21: [77.5, 83.7, 89.8], 24: [80.0, 86.4, 92.9],
  },
};

/** Значения нормы для возраста в месяцах (линейно между точками таблицы). */
export function whoAt(band: Band, months: number): [number, number, number] | null {
  const keys = Object.keys(band).map(Number).sort((a, b) => a - b);
  if (months < keys[0]! || months > keys[keys.length - 1]!) return null;
  for (let i = 0; i < keys.length - 1; i++) {
    const a = keys[i]!;
    const b = keys[i + 1]!;
    if (months >= a && months <= b) {
      const t = b === a ? 0 : (months - a) / (b - a);
      const va = band[a]!;
      const vb = band[b]!;
      return [0, 1, 2].map((k) => va[k]! + (vb[k]! - va[k]!) * t) as [number, number, number];
    }
  }
  return [...band[keys[keys.length - 1]!]!] as [number, number, number];
}
